import { merge, get } from 'lodash-es';
import { CHART_TYPES } from '../enums';
import { DEFAULT_COLORS, translateColorArray } from './dataset-color-config-builder';

const DEFAULT_DATASET_STYLE = {
  hoverOffset: 4,
  cutout: '50%',
  radius: 87,
  borderRadius: 0
};

const DEFAULT_CONFIG = {
  options: {
    elements: {
      borderRadius: 0,
      arc: {
        borderWidth: 0,
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded',
          pointRadius: 5,
          pointBorderColor: 'none'
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true
  },
  type: 'doughnut',
};

const getColors = (config, chartJSConfig, context) => {
  // colors from chartJSConfig have priority in this case as they mean they were defined by the user.
  const colorsDefinedByUser = get(chartJSConfig, 'data.datasets[0].backgroundColor') || [];
  // colorsDefinedByUser.length > 1 because it happens only when users define multiple colors for different slices
  return colorsDefinedByUser.length > 1 ?
    colorsDefinedByUser : translateColorArray(DEFAULT_COLORS, context);
};

export default (config, chartJSConfig, context) => {
  const { series: allSeries = [] } = config;

  const series = allSeries.filter(series =>
    [CHART_TYPES.DOUGHNUT.toUpperCase()].includes(series.seriesType));

  if (!series.length) return chartJSConfig;

  const data = extractDataForDoughnutFromDataset(series);
  data.datasets[0].backgroundColor = getColors(config, chartJSConfig, context);
  const merged = {
    ...DEFAULT_CONFIG,
    data: data,
  };

  // in the case doughnut with defined with multiple series, we have to remove all the series except the first one
  // since at this stage all the series data has been merged to the first one and the others are redundant (and break
  // the chart)
  chartJSConfig.data.datasets = [chartJSConfig.data.datasets[0]];
  merge(chartJSConfig, merged);
  delete chartJSConfig.options.scales;
  return chartJSConfig;
};

const extractDataForDoughnutFromDataset = series => {
  const newDataset = {
    labels: [],
    datasets: [{
      ...DEFAULT_DATASET_STYLE,
      data: [],
    }]
  };

  const rawDatasets = {};

  // doughnut with multiple series means that each series represents a slice of the doughnut
  if (series.length > 1) {
    series.forEach(s => {
      const { aggregation: { data } } = s;
      if (!data) {
        return;
      }
      const [dataPoint] = data;
      // adding formattedValue for tooltip
      const { value, style, aggregationLabel, formattedValue } = dataPoint;
      rawDatasets[aggregationLabel] = { value, style, formattedValue };
    });
  } else {
    // one series means that a dimension was defined, and datasets will be created out of that dimension values
    const [dataset] = series;
    dataset.aggregation.data.forEach(dataPoint => {
      const [timeDimension] = dataPoint.dimensions;
      const { value, style, formattedValue } = dataPoint;
      rawDatasets[timeDimension] = rawDatasets[timeDimension] ? rawDatasets[timeDimension] : {
        value: 0,
        style,
        formattedValue
      };
      rawDatasets[timeDimension].value += value;
    });
  }

  Object.entries(rawDatasets).forEach(([key, value], index) => {
    if (value.value !== 0) {
      newDataset.labels.push(key);
      newDataset.datasets[0].data.push({
        value: value.value, formattedValue: value.formattedValue
      });
      if (value.style) {
        // eslint-disable-next-line no-loops/no-loops
        for (const styleName in value.style) {
          if (Object.prototype.hasOwnProperty.call(value.style, styleName)) {
            if (!newDataset.datasets[0][styleName]) {
              newDataset.datasets[0][styleName] = [];
            }
            newDataset.datasets[0][styleName][index] = value.style[styleName];
          }
        }
      }
    }
  });
  return newDataset;
};
